// src/components/chat/PlusMenuButton.tsx
import * as React from "react";
import { useAgent } from "../../contexts/AgentContext";
import { useEntitlements } from "../../hooks/useEntitlements";
import { createLogger } from "../../utils/logger";

// Import icon SVGs from assets
import AddFileIcon from "../../../assets/Add File.svg";
import NewConversationIcon from "../../../assets/NewConversation.svg";
import DocumentLibraryIcon from "../../../assets/DocumentLibrary.svg";
import { track } from "../../utils/analytics";

const log = createLogger("PlusMenuButton");

interface PlusMenuButtonProps {
  onNewConversation: () => void;
  onFilesSelected?: (files: File[]) => void;
  onBrowseLibrary?: () => void;
  className?: string;
}

/**
 * Plus button with dropdown menu for chat actions
 * Now agent-aware: enables file upload only for contract-qa agent
 */
const PlusMenuButton: React.FC<PlusMenuButtonProps> = ({
  onNewConversation,
  onFilesSelected,
  onBrowseLibrary,
  className = "",
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Get current agent features from context
  const { currentAgent, currentAgentFeatures } = useAgent();

  // Check if file upload is enabled for current agent
  const isFileUploadEnabled = currentAgentFeatures.allowFileUpload;

  // Document Library is a per-tenant entitlement, not an agent feature
  const { documentLibrary: isDocumentLibraryEnabled } = useEntitlements();

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isMenuOpen]);

  // Handle new conversation
  const handleNewConversation = () => {
    log.info("New conversation clicked");
    track("chat:new_conversation", {
      agent_id: currentAgent.id,
    });
    setIsMenuOpen(false);
    onNewConversation();
  };

  // Handle add file click
  const handleAddFile = () => {
    if (!isFileUploadEnabled) {
      log.info("Add file clicked but not enabled for agent", {
        agentId: currentAgent.id,
        agentName: currentAgent.name,
      });
      return;
    }

    log.info("Add file clicked - opening file picker", {
      agentId: currentAgent.id,
      supportedTypes: currentAgentFeatures.supportedFileTypes || [],
      maxFileCount: currentAgentFeatures.maxFileCount,
    });

    track("chat:files_attach_clicked", {
      agent_id: currentAgent.id,
      supported_types: currentAgentFeatures.supportedFileTypes || [],
      max_file_count: currentAgentFeatures.maxFileCount,
    });

    setIsMenuOpen(false);

    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection from file picker
  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;

    if (selectedFiles && selectedFiles.length > 0) {
      const filesArray = Array.from(selectedFiles);

      log.info("Files selected", {
        count: filesArray.length,
        fileNames: filesArray.map((f) => f.name),
        fileSizes: filesArray.map((f) => f.size),
      });

      track("chat:files_attached", {
        count: filesArray.length,
        file_types: filesArray.map((f) => f.name.split(".").pop() || "unknown"),
        file_bytes: filesArray.map((f) => f.size),
      });

      if (onFilesSelected) {
        onFilesSelected(filesArray);
      }
    }

    // Reset input value to allow selecting the same file again
    event.target.value = "";
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Plus icon that scales on hover
  const PlusIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transition: "transform 0.2s ease",
        transform: isHovered ? "scale(1.8)" : "scale(1.5)",
      }}
    >
      <path
        d="M12 5V19M5 12H19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Determine file input attributes based on agent configuration
  const getFileInputProps = () => {
    const props: React.InputHTMLAttributes<HTMLInputElement> = {
      type: "file",
      style: { display: "none" },
      "aria-label": "Upload template files",
    };

    // Set accept attribute based on agent's supported file types
    if (
      currentAgentFeatures.supportedFileTypes &&
      currentAgentFeatures.supportedFileTypes.length > 0
    ) {
      props.accept = currentAgentFeatures.supportedFileTypes.join(",");
    }

    // Only allow multiple selection if maxFileCount is not 1
    if (currentAgentFeatures.maxFileCount !== 1) {
      props.multiple = true;
    }

    return props;
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }} className={className}>
      {/* Hidden file input with agent-aware attributes */}
      <input ref={fileInputRef} {...getFileInputProps()} onChange={handleFileSelection} />

      {/* Plus Button */}
      <button
        onClick={toggleMenu}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Chat actions"
        aria-label="Chat actions menu"
        style={{
          backgroundColor: isHovered ? "rgba(0, 102, 204, 0.1)" : "#f5f5f5",
          border: "1px solid #e0e0e0",
          borderRadius: "50%",
          width: "26px",
          height: "26px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: isHovered ? "#0066cc" : "#666",
          transition: "all 0.2s ease",
          boxShadow: isHovered ? "0 2px 4px rgba(0, 0, 0, 0.1)" : "none",
        }}
      >
        <PlusIcon />
      </button>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "0",
            marginBottom: "8px",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e0e0e0",
            minWidth: "250px",
            zIndex: 1000,
            overflow: "hidden",
            fontFamily: "'Segoe UI', sans-serif",
          }}
        >
          {/* Browse Document Library Option - gated on the per-tenant documentLibrary entitlement */}
          {isDocumentLibraryEnabled && (
            <button
              onClick={() => {
                setIsMenuOpen(false);
                if (onBrowseLibrary) {
                  onBrowseLibrary();
                }
                log.info("Browse library clicked");
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 16px",
                backgroundColor: "transparent",
                border: "none",
                borderBottom: "1px solid #f0f0f0",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = "#f9f9f9";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
              }}
            >
              <DocumentLibraryIcon width="20" height="20" />
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <span
                  style={{ fontSize: "14px", color: "#333", fontFamily: "'Segoe UI', sans-serif" }}
                >
                  Browse Document Library
                </span>
                <span style={{ fontSize: "11px", color: "#666" }}>
                  Select from standard templates
                </span>
              </div>
            </button>
          )}

          {/* Add File Option - Enabled for contract-qa agent */}
          <button
            onClick={handleAddFile}
            disabled={!isFileUploadEnabled}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px 16px",
              backgroundColor: "transparent",
              border: "none",
              borderBottom: "1px solid #f0f0f0",
              cursor: isFileUploadEnabled ? "pointer" : "not-allowed",
              transition: "background-color 0.2s ease",
              textAlign: "left",
              opacity: isFileUploadEnabled ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isFileUploadEnabled) {
                (e.currentTarget as HTMLElement).style.backgroundColor = "#f9f9f9";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
            }}
          >
            <AddFileIcon />
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span
                style={{ fontSize: "14px", color: "#333", fontFamily: "'Segoe UI', sans-serif" }}
              >
                {currentAgent.id === "drafting-assistant"
                  ? "Add Context"
                  : currentAgent.id === "contract-qa"
                    ? "Add Template"
                    : "Add File"}
              </span>
              {!isFileUploadEnabled && (
                <span style={{ fontSize: "12px", color: "#999", fontStyle: "italic" }}>
                  Coming soon
                </span>
              )}
              {isFileUploadEnabled && (
                <span style={{ fontSize: "11px", color: "#666" }}>
                  {currentAgent.id === "drafting-assistant"
                    ? "Upload specs, SOW draft & Scope"
                    : currentAgent.id === "contract-qa"
                      ? ".docx with standard positions"
                      : currentAgentFeatures.supportedFileTypes?.join(", ") || ""}
                </span>
              )}
            </div>
          </button>

          {/* New Conversation Option - Always enabled */}
          <button
            onClick={handleNewConversation}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px 16px",
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              textAlign: "left",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "#f5f5f5";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
            }}
          >
            <NewConversationIcon />
            <span style={{ fontSize: "14px", color: "#333", fontFamily: "'Segoe UI', sans-serif" }}>
              New Conversation
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default PlusMenuButton;
